import axios from "axios";
import { globalErrorHandler } from "../utils/ErrorHandler";
import { encryptMsalValue, decryptMsalValue } from "../utils/msalStorage";

// Base API URLs
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AGENT_BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL;
const CSRF_TOKEN_ENDPOINT = "/api/Auth/csrf-token";

// CSRF Token Management with Expiration Handling
class CSRFTokenManager {
  token = null;
  tokenExpiryTime = null;
  isRefreshing = false;
  refreshPromise = null;
  TOKEN_LIFETIME = 10 * 60 * 1000; // Backend CSRF token lifetime: 15 minutes (using 10 min to refresh early)

  /**
   * Get CSRF token - fetches new one if expired or doesn't exist
   * @param {Object} authHeaders - Authorization headers for CSRF fetch
   * @returns {Promise<string>} CSRF token
   */
  async getToken(authHeaders) {
    // Check if token exists and is still valid
    if (this.token && this.isTokenValid()) {
      return this.token;
    }

    // Token expired or doesn't exist
    if (this.token && !this.isTokenValid()) {
      this.clearToken();
    }

    // If already refreshing, wait for that promise
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    return this.refreshToken(authHeaders);
  }

  /**
   * Check if current token is still valid
   * @returns {boolean} True if token is valid
   */
  isTokenValid() {
    if (!this.tokenExpiryTime) {
      return false;
    }

    const now = Date.now();
    const isValid = now < this.tokenExpiryTime;

    return isValid;
  }

  /**
   * Fetch new CSRF token from backend
   * @param {Object} authHeaders - Authorization headers
   * @returns {Promise<string>} New CSRF token
   */
  async refreshToken(authHeaders) {
    this.isRefreshing = true;

    this.refreshPromise = axios
      .get(`${BASE_URL}${CSRF_TOKEN_ENDPOINT}`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      })
      .then((response) => {
        this.token = response.data?.token ?? response.data?.csrfToken;
        this.tokenExpiryTime = Date.now() + this.TOKEN_LIFETIME;
        
        this.isRefreshing = false;
        this.refreshPromise = null;
        return this.token;
      })
      .catch((error) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Clear stored CSRF token
   */
  clearToken() {
    this.token = null;
    this.tokenExpiryTime = null;
  }

  /**
   * Get remaining token lifetime in minutes
   * @returns {number} Minutes until token expires
   */
  getRemainingLifetime() {
    if (!this.tokenExpiryTime) {
      return 0;
    }

    const remaining = this.tokenExpiryTime - Date.now();
    return Math.max(0, Math.floor(remaining / 60000));
  }
}

const csrfManager = new CSRFTokenManager();

// Auto-logout timer reset function (set from Layout component)
let globalResetTimer = null;

/**
 * Set the auto-logout reset function to be called on API requests
 * @param {Function} resetFn - Function to reset the auto-logout timer
 */
export const setAutoLogoutReset = (resetFn) => {
  globalResetTimer = resetFn;
};

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  try {
    // Get all keys from sessionStorage
    const keys = Object.keys(sessionStorage);
    
    // Look for msal token
    const mslKeys = keys.filter(k => k.includes("msal") && k.includes("access_token"));
    
    if (mslKeys.length > 0) {
      const tokenData = decryptMsalValue(sessionStorage.getItem(mslKeys[0]));
      if (tokenData) {
        const parsed = safeJsonParse(tokenData);
        return parsed?.secret ?? parsed?.access_token ?? null;
      }
    }

    // Fallback: try the original method
    const idTokenKey = decryptMsalValue(
      sessionStorage.getItem(
        `msal.token.keys.${process.env.REACT_APP_MSAL_CLIENT_ID}`
      )
    );

    if (!idTokenKey) {
      return null;
    }

    const parsedIdTokenKey = safeJsonParse(idTokenKey);
    if (!parsedIdTokenKey?.idToken?.length) {
      return null;
    }

    const token = decryptMsalValue(
      sessionStorage.getItem(`${parsedIdTokenKey.idToken[0]}`)
    );

    if (!token) {
      return null;
    }

    const parsedToken = safeJsonParse(token);
    return encryptMsalValue(parsedToken?.secret ?? null);
  } catch {
    return null;
  }
};

// Create API instances
const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Handle unauthorized response
const handleUnauthorized = () => {
  csrfManager.clearToken();
  globalErrorHandler.handleUnauthorized();
};

// Request interceptor - FIRST: Add Auth Token
const addAuthTokenInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      
      if (token) {
        config.headers.Authorization = `${token}`;
      }
      
      // Reset auto-logout timer on API calls (user activity)
      if (globalResetTimer && typeof globalResetTimer === 'function') {
        globalResetTimer();
      }
      
      return config;
    },
    (error) => {
      throw error;
    }
  );
};

// CSRF Token Interceptor - SECOND: Add CSRF token to state-changing requests
const addCSRFTokenInterceptor = (client) => {
  client.interceptors.request.use(
    async (config) => {
      const stateChangingMethods = ["post", "put", "delete", "patch"];

      if (stateChangingMethods.includes(config.method?.toLowerCase())) {
        const token = getAuthToken();
        
        if (!token) {
          throw new Error("No authentication token");
        }

        // Get CSRF token (will auto-refresh if expired)
        const csrfToken = await csrfManager.getToken({
          Authorization: `${token}`,
        });
        
        config.headers["X-CSRF-Token"] = csrfToken;
      }

      return config;
    },
    (error) => {
      throw error;
    }
  );
};

// Response interceptor - Handle errors with global error handler
const addResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      const originalRequest = error.config;

      // Use global error handler for safe error logging
      globalErrorHandler.handleError(error, 'API Response');

      if (status === 401) {
        handleUnauthorized();
        throw error;
      }

      // Handle 403 Forbidden - likely invalid/expired CSRF token
      if (status === 403) {
        const shouldClearCSRF = globalErrorHandler.handleForbidden(error);
        
        // Retry logic: Clear CSRF and retry request once
        if (shouldClearCSRF && !originalRequest._csrfRetry) {
          // Mark request to prevent infinite retry loop
          originalRequest._csrfRetry = true;
          
          // Clear expired/invalid token
          csrfManager.clearToken();
          
          const token = getAuthToken();
          
          if (!token) {
            throw error;
          }

          // Fetch fresh CSRF token
          const newCsrfToken = await csrfManager.refreshToken({
            Authorization: `${token}`,
          });
          
          // Update request header with new token
          originalRequest.headers["X-CSRF-Token"] = newCsrfToken;
          
          // Retry the original request
          return client(originalRequest);
        }
      }

      throw error;
    }
  );
};

// Create agent API instance
const agentApiInstance = axios.create({
  baseURL: AGENT_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth interceptor to agent API
addAuthTokenInterceptor(agentApiInstance);

// Simple error handler for agent API
agentApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }

    const status = error.response?.status;
    const errorCode = error.code;

    if (status === 404) {
      throw new Error(
        "Query endpoint not found. Please check if the service is running."
      );
    }
    if (status === 400) {
      throw new Error(
        "Invalid query parameters. Please check your input."
      );
    }
    if (status && status >= 500) {
      throw new Error(
        "Server error occurred while processing your query. Please try again later."
      );
    }
    if (errorCode === "ECONNABORTED") {
      throw new Error(
        "Query request timed out. The service might be experiencing high load."
      );
    }
    if (!error.response && error.request) {
      throw new Error(
        "No response received from the query service. Please check your network connection."
      );
    }

    throw error;
  }
);

// Apply interceptors in CORRECT ORDER
addAuthTokenInterceptor(axiosClient);
addCSRFTokenInterceptor(axiosClient);
addResponseInterceptor(axiosClient);
addResponseInterceptor(agentApiInstance);

// Export clients and CSRF manager
export default axiosClient;
export { agentApiInstance, csrfManager };